"use client";

import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { PaginatedResponse, Sku, GetSkuListQuery } from "@chipi-stack/types";
import { useChipiContext } from "../context";
import { ChipiApiError } from "@chipi-stack/shared";

type SkuListInput = {
  query: GetSkuListQuery;
  getBearerToken?: () => Promise<string | null | undefined>;
  queryOptions?: Omit<UseQueryOptions<PaginatedResponse<Sku>, Error>, 'queryKey' | 'queryFn'>;
};

export function useGetSkuList(input?: SkuListInput) {
  const { chipiSDK } = useChipiContext();
  const queryClient = useQueryClient();

  const buildQueryKey = (q?: GetSkuListQuery) => [
    "available-skus",
    q?.page ?? 1,
    q?.limit ?? 10,
    q?.offset ?? 0,
    q?.provider ?? null,
    q?.category ?? null,
  ];
  
  const query = useQuery<PaginatedResponse<Sku>, Error>({
    queryKey: buildQueryKey(input?.query),
    queryFn: async () => {
      if (!input?.getBearerToken) throw new Error("getBearerToken is required");
      const bearerToken = await input.getBearerToken();
      if (!bearerToken) throw new Error("Bearer token is required");
      return chipiSDK.getSkuList(input.query, bearerToken);
    },
    enabled: Boolean(input?.getBearerToken),
    retry: (failureCount: number, error: Error) => {
      // Don't retry on API errors (errors with status codes like 404, 400, etc.)
      if (error instanceof ChipiApiError || (error as any)?.status) {
        return false;
      }

      return failureCount < 3;
    },
    ...input?.queryOptions
  });

  const fetchSkuList = async (newInput: SkuListInput) => {
    return queryClient.fetchQuery({
      queryKey: buildQueryKey(newInput?.query),
      queryFn: async () => {
        if (!newInput?.getBearerToken) throw new Error("getBearerToken is required");
        const bearerToken = await newInput.getBearerToken();
        if (!bearerToken) throw new Error("Bearer token is required");
        return chipiSDK.getSkuList(newInput.query, bearerToken);
      },
      retry: (failureCount: number, error: Error) => {
        // Don't retry on API errors (errors with status codes like 404, 400, etc.)
        if (error instanceof ChipiApiError || (error as any)?.status) {
          return false;
        }
        // Retry on network errors
        return failureCount < 3;
      },
    });
  };

  return {
    ...query,
    fetchSkuList,
  };
}
